import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { HeadersEnum, ParamsKeys, ParamsValues, RestURLsEnum } from '@app/model/http-request-enums';
import { Cart, CartNode, PlaceOrderRequest } from '@app/model/cart.model';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { CartProvider } from '@app/core/cart-provider';
import { LocalStorageService } from '@app/core/local-storage/local-storage.service';
import { catchError, take, tap } from 'rxjs/operators';
import { UpdateCart } from '@app/store/cart/cart.actions';
import { Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Injectable({
    providedIn: 'root'
})
export class CartService {
    csCartUrl = environment.csBaseURL + RestURLsEnum.CART_BASE;
    header = new HttpHeaders({
        'Accept': [
            HeadersEnum.ACCEPT_CART,
            HeadersEnum.ACCEPT_ERROR,
        ]
    });

    constructor(
        public httpClient: HttpClient,
        private cartProvider: CartProvider,
        private storage: LocalStorageService,
        private notifier: NzNotificationService
    ) { }

    get$(id: string) {
        const url = `${this.csCartUrl}${id}`;
        const params: HttpParams = new HttpParams()
            .set(ParamsKeys.INCLUDE,
                [ParamsValues.CATALOGUE_ITEMS, ParamsValues.PRICES].join(','))
            .set(ParamsKeys.SORT_BY,
                [
                    ParamsValues.CART_GROUP_TYPE,
                    ParamsValues.CART_GROUP_CREATION_INSTANT,
                    ParamsValues.CREATION_INSTANT_DESC,
                    ParamsValues.CREATION_GRP_INSTANT_DESC
                ].join(','));

        return this.httpClient.get<CartNode>(url, {
            params: params,
            headers: this.header
        });

    }

    patchGroupName$(cartId: string, groupId: number, groupName: string) {
        const editGroupNameUrl = this.csCartUrl + `${cartId}/group/${groupId}`;

        const editGroupNameParams: HttpParams = new HttpParams()
            .set('name', groupName)
            .append(ParamsKeys.INCLUDE,
                [ParamsValues.CATALOGUE_ITEMS, ParamsValues.PRICES].join(','));

        return this.httpClient.patch<Cart>(editGroupNameUrl, '', {
            params: editGroupNameParams,
            headers: this.header
        });
    }

    placeOrder$(cartId: string, payload: PlaceOrderRequest) {
        const placeOrderURL = environment.csBaseURL + '/cart/' + cartId + '/place-order';
        this.header = this.header.set('Content-Type', 'application/json');

        return this.httpClient.post<CartNode>(placeOrderURL, JSON.stringify(payload), {
            headers: this.header
        });
    }


    newProductGroupSuggestion$(cartId: string, catalogueId: number) {
        const header = new HttpHeaders({
            'Accept': [
                HeadersEnum.ACCEPT_CART_GROUP_SUGGESTION_LIST,
                HeadersEnum.ACCEPT_ERROR,
            ]
        });

        const params: HttpParams = new HttpParams()
            .set(ParamsKeys.SORT_BY,
                ParamsValues.CART_GROUP_SUGGESTION_CREATION_INSTANT)
            .append('itemId', `${catalogueId}`);

        const url = this.csCartUrl + `${cartId}/group-suggestion`;

        return this.httpClient.get(url, {
            params: params,
            headers: header
        });
    }

    removeGroup$(cartId: string, groupId: number): Observable<Cart> {
        const deleteCartGroupUrl = this.csCartUrl + `${cartId}/group/${groupId}`;
        const deleteCartGroupParams: HttpParams = new HttpParams()
            .set(ParamsKeys.INCLUDE,
                [ParamsValues.CATALOGUE_ITEMS, ParamsValues.PRICES].join(','))
            .append(ParamsKeys.SORT_BY,
                [ParamsValues.CART_GROUP_TYPE,
                    ParamsValues.CART_GROUP_CREATION_INSTANT,
                    ParamsValues.CREATION_INSTANT_DESC,
                    ParamsValues.CREATION_GRP_INSTANT_DESC].join(','));
        return this.httpClient.delete<Cart>(deleteCartGroupUrl, {
            params: deleteCartGroupParams,
            headers: this.header
        });
    }

    loadNewCart(): Observable<CartNode> {
        this.storage.removeItem(environment.cartId, true);
        return this.cartProvider.createCart()
            .pipe(
                tap((cart: CartNode) => this.storage.setItem(environment.cartId, cart.id, true, false)),
                catchError(e => {
                    this.notifier.error('Something bad went wrong!', 'Please reload the page or try again in few minutes.',
                        { nzDuration: 0 });
                    return [e];
                })
            );
    }

}
